import React from "react";
import { PhoneCall, Save } from "lucide-react";
import { toast } from "sonner";
import { useGet, apiPut } from "../../hooks/useApi.js";

type CallIntelSettings = {
  mode: "shadow" | "recommend" | "auto";
  killSwitch: boolean;
  matchDecisiveThreshold: number;
  matchRecommendThreshold: number;
  matchReviewFloor: number;
  matchMargin: number;
};

const DEFAULTS = { matchDecisiveThreshold: 0.97, matchRecommendThreshold: 0.78, matchReviewFloor: 0.55, matchMargin: 0.15 };

type Field = "matchDecisiveThreshold" | "matchRecommendThreshold" | "matchReviewFloor" | "matchMargin";

const FIELDS: Array<{ key: Field; label: string; min: number; max: number; step: number; help: string; preview: (v: number) => string }> = [
  {
    key: "matchDecisiveThreshold", label: "Decisive threshold", min: 0, max: 1, step: 0.01,
    help: "Calls with explicit ticket evidence auto-draft when confidence is at or above this value (and the margin holds).",
    preview: (v) => `Auto-draft at decisive evidence ≥ ${v.toFixed(2)}`,
  },
  {
    key: "matchRecommendThreshold", label: "Recommend threshold", min: 0, max: 1, step: 0.01,
    help: "Contextual matches at or above this confidence are recommended to a reviewer instead of left ambiguous.",
    preview: (v) => `Recommend to a reviewer at ≥ ${v.toFixed(2)}`,
  },
  {
    key: "matchReviewFloor", label: "Review floor", min: 0, max: 1, step: 0.01,
    help: "Candidates below this confidence are not considered at all — the call stays unmatched.",
    preview: (v) => `Ignore candidates below ${v.toFixed(2)} (unmatched)`,
  },
  {
    key: "matchMargin", label: "Required margin", min: 0, max: 0.5, step: 0.01,
    help: "The top candidate must beat the runner-up by at least this much before it is selected or recommended.",
    preview: (v) => `Top candidate must lead by ≥ ${v.toFixed(2)}`,
  },
];

/**
 * Admin card for the call-to-ticket match confidence ladder. Reads and writes
 * GET/PUT /api/v1/calls/settings (admin/owner only on the API side).
 */
export function CallIntelSettingsCard({ editable }: { editable: boolean }) {
  const q = useGet<CallIntelSettings>("/api/v1/calls/settings");
  const [v, setV] = React.useState<CallIntelSettings | null>(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (q.data) setV(q.data);
  }, [q.data]);
  if (q.error)
    return (
      <section className="rounded-2xl border border-red-300 p-5">
        <h2 className="font-semibold">Call intelligence settings unavailable</h2>
        <p className="mt-1 text-sm text-red-600">{String(q.error)}</p>
        <button onClick={q.refetch} className="mt-3 rounded-lg border px-3 py-2 text-sm">Retry</button>
      </section>
    );
  if (!v)
    return (
      <div className="rounded-2xl border p-5" role="status">
        Loading call-intelligence settings…
      </div>
    );

  const floor = v.matchReviewFloor;
  const recommend = v.matchRecommendThreshold;
  const decisive = v.matchDecisiveThreshold;
  const ordered = floor < recommend && recommend < decisive;
  const rangesOk = FIELDS.every((f) => {
    const value = v[f.key];
    return value >= f.min && value <= f.max;
  });
  const dirty = FIELDS.some((f) => v[f.key] !== (q.data?.[f.key] ?? DEFAULTS[f.key]));
  const canSave = editable && ordered && rangesOk && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await apiPut("/api/v1/calls/settings", {
        matchDecisiveThreshold: decisive,
        matchRecommendThreshold: recommend,
        matchReviewFloor: floor,
        matchMargin: v.matchMargin,
      });
      toast.success("Match thresholds saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save match thresholds");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden"
      aria-labelledby="call-intel-settings-title"
    >
      <div className="border-b border-[hsl(var(--border))] px-5 py-4">
        <div className="flex items-center gap-2">
          <PhoneCall className="h-4 w-4 text-brand-600" />
          <h2 id="call-intel-settings-title" className="font-semibold">
            Call intelligence — ticket match thresholds
          </h2>
        </div>
        <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
          Confidence ladder used when matching incoming calls to tickets. Applied to new call
          processing; existing matches are unchanged.
        </p>
      </div>
      <div className="space-y-6 p-5">
        <fieldset disabled={!editable} className="space-y-5 disabled:opacity-70">
          <legend className="sr-only">Match thresholds</legend>
          {FIELDS.map((f) => (
            <div key={f.key} data-testid={`threshold-${f.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor={f.key} className="text-sm font-medium">
                  {f.label}
                  <span className="block text-xs font-normal text-[hsl(var(--content-subtle))]">{f.help}</span>
                </label>
                <output htmlFor={f.key} className="font-mono text-sm tabular-nums" aria-live="polite">
                  {v[f.key].toFixed(2)}
                </output>
              </div>
              <input
                id={f.key}
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={v[f.key]}
                disabled={!editable}
                onChange={(e) => setV({ ...v, [f.key]: Number(e.target.value) })}
                aria-describedby={f.key + "-preview"}
                className="mt-2 w-full"
              />
              <p id={f.key + "-preview"} className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
                {f.preview(v[f.key])}
              </p>
            </div>
          ))}
        </fieldset>
        {!ordered && (
          <p role="alert" className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-600 dark:text-red-400">
            The ladder must be ordered: review floor ({floor.toFixed(2)}) &lt; recommend ({recommend.toFixed(2)}) &lt; decisive ({decisive.toFixed(2)}). Adjust the sliders until each threshold is strictly above the one below it.
          </p>
        )}
        {!rangesOk && (
          <p role="alert" className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-600 dark:text-red-400">
            Thresholds must stay within their allowed ranges (0–1; required margin 0–0.5).
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? "Saving…" : "Save thresholds"}
          </button>
          {dirty && ordered && rangesOk && (
            <span className="text-xs text-[hsl(var(--content-subtle))]">Unsaved changes</span>
          )}
        </div>
      </div>
    </section>
  );
}
